import { useRef, type RefObject } from 'react';
import { useGT } from 'gt-react';
import { useToast } from '@/context/ToastContext';
import type { GoToWorksheetCell } from '../useWorksheetCellJump';
import {
  useCellNotShownNotice,
  useReviewCellJumpTarget,
  type ReviewCellTarget,
} from './reviewCellJump';

/**
 * Where a comment's row is under the grid's filters and sort, on its own
 * worksheet: the page and the grid row key there. Null when they hide it.
 */
export type LocateCommentRow = (
  target: ReviewCellTarget,
) => Promise<{ page: number; rowKey: string } | null>;

/** Opens a row's cell comments in the grid on screen; false when it can't. */
interface CommentCellOpener {
  showComments: (rowKey: string, field: string) => boolean;
}

/**
 * Makes the review grid where the page's jumps to a comment's cell land
 * ({@link useJumpToReviewCell}): find the row's page under the grid's filters
 * and sort, go there through the grid's open-then-select (switching worksheet
 * tab and page as needed), then select the cell and open its comments. A row
 * the grid can't show — filtered out, in a division not selected, or hidden —
 * is said in a popup. Takes no jumps without `locate` (the item catalog).
 */
export function useReviewCommentJump({
  locate,
  goToCell,
  grid,
}: {
  locate: LocateCommentRow | undefined;
  goToCell: GoToWorksheetCell;
  grid: RefObject<CommentCellOpener | null>;
}): void {
  const gt = useGT();
  const { showToast } = useToast();
  const notShown = useCellNotShownNotice();

  const lookUpFailed = () =>
    showToast({
      title: gt('Could not find the cell', {
        context:
          "toast title: looking up where a comment's cell is in the grid failed",
      }),
      message: gt('Try again in a moment.', {
        context: "toast body after looking up a comment's cell failed",
      }),
      color: 'warning',
    });

  // The lookups can finish out of order: only the latest jump asked for may
  // land, or a slow earlier one would take the reviewer back to its cell.
  const latestJump = useRef(0);

  const jump = async (target: ReviewCellTarget) => {
    if (!locate) return notShown();
    const jumpId = ++latestJump.current;
    const located = await locate(target);
    if (jumpId !== latestJump.current) return;
    if (!located) return notShown();
    goToCell({
      group: target.group,
      page: located.page,
      arrive: () => {
        if (!grid.current?.showComments(located.rowKey, target.columnKey)) {
          notShown();
        }
      },
    });
  };

  useReviewCellJumpTarget(
    locate
      ? (target) => {
          jump(target).catch(lookUpFailed);
        }
      : undefined,
  );
}
